/**
 * MemoryMonitor — Debug overlay สำหรับวัด memory leak
 *
 * เปิด: กด Ctrl+Shift+M หรือ ?memmon=1 ใน URL
 *
 * Console:
 *   window.__memMonitor.snapshot('label')
 *   window.__memMonitor.history()
 *   window.__memMonitor.detectLeak()
 *   window.__memMonitor.toggle()
 *
 * Sentry:
 *   window.__memMonitor.setSentryTags({ pin: '528506', email: 'admin@vonder.io' })
 */

let snapshotHistory = [];
let overlayEl = null;
let intervalId = null;
let trackedPixiApp = null;
let keyHandler = null;
let isActive = false;
let sentryTags = {};
let leakReportCooldown = false;

// ─── Sentry ──────────────────────────────────────────────

/**
 * ตั้ง Sentry global tags + user context ให้ทุก event (crash, memory, ฯลฯ) หาง่าย
 * เรียกจาก initMemoryMonitor (auto) หรือ window.__memMonitor.setSentryTags()
 */
function setSentryTags(tags) {
  sentryTags = { ...sentryTags, ...tags };

  // Set Sentry global scope — ทำให้ ALL events (แม้แต่ crash) มี tag นี้
  try {
    import("@sentry/react").then((Sentry) => {
      if (sentryTags.pin) Sentry.setTag("pin", sentryTags.pin);
      if (sentryTags.challengeID) Sentry.setTag("challengeID", sentryTags.challengeID);
      if (sentryTags.challengeCode) Sentry.setTag("challengeCode", sentryTags.challengeCode);
      if (sentryTags.theme) Sentry.setTag("theme", sentryTags.theme);
      if (sentryTags.email) {
        Sentry.setTag("email", sentryTags.email);
        Sentry.setUser({ email: sentryTags.email });
      }
      Sentry.setTag("feature", "vonder-go");
    }).catch(() => {});
  } catch (e) {}
}

function extractPinFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("pin") || null;
  } catch (e) {
    return null;
  }
}

function extractEmailFromToken() {
  try {
    const token = localStorage.getItem("user_token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.username || payload?.email || null;
  } catch (e) {
    return null;
  }
}

function reportToSentry(label, data) {
  try {
    import("@sentry/react").then((Sentry) => {
      Sentry.captureMessage(`[MemoryMonitor] ${label}`, {
        level: data.level || "info",
        tags: {
          feature: "memory-monitor",
          status: data.suspectLeak ? "leak" : "healthy",
          ...sentryTags,
        },
        extra: {
          ...data,
          timestamp: new Date().toISOString(),
          snapshotCount: snapshotHistory.length,
        },
      });
    }).catch(() => {});
  } catch (e) {}
}

// ─── Core ────────────────────────────────────────────────

function takeSnapshot(label = "") {
  const mem = performance.memory || {};
  const textures = trackedPixiApp
    ? Object.keys(trackedPixiApp.renderer?.texture?.managedTextures || {}).length
    : 0;

  const snapshot = {
    time: new Date().toLocaleTimeString(),
    label,
    jsHeapUsed: mem.usedJSHeapSize
      ? (mem.usedJSHeapSize / 1048576).toFixed(1)
      : "N/A",
    jsHeapTotal: mem.totalJSHeapSize
      ? (mem.totalJSHeapSize / 1048576).toFixed(1)
      : "N/A",
    pixiTextures: textures,
  };

  snapshotHistory.push(snapshot);
  if (snapshotHistory.length > 50) snapshotHistory.shift();

  return snapshot;
}

function renderOverlay() {
  if (!overlayEl) {
    overlayEl = document.createElement("div");
    overlayEl.id = "memory-monitor";
    Object.assign(overlayEl.style, {
      position: "fixed",
      top: "8px",
      right: "8px",
      zIndex: "99999",
      background: "rgba(0,0,0,0.85)",
      color: "#0f0",
      fontFamily: "monospace",
      fontSize: "11px",
      padding: "8px 10px",
      borderRadius: "6px",
      pointerEvents: "none",
      minWidth: "220px",
      lineHeight: "1.5",
      whiteSpace: "pre",
    });
    document.body.appendChild(overlayEl);
  }

  const latest = snapshotHistory[snapshotHistory.length - 1] || {};
  const first = snapshotHistory[0] || {};

  const heapDelta =
    latest.jsHeapUsed !== "N/A" && first.jsHeapUsed !== "N/A"
      ? (parseFloat(latest.jsHeapUsed) - parseFloat(first.jsHeapUsed)).toFixed(1)
      : "N/A";

  overlayEl.innerHTML = [
    `⏱ ${latest.time || "--"}`,
    `🖼 Textures: ${latest.pixiTextures ?? "--"}`,
    `📦 Heap Used: ${latest.jsHeapUsed ?? "--"} MB`,
    `📈 Heap Total: ${latest.jsHeapTotal ?? "--"} MB`,
    `📊 Heap Δ: ${heapDelta} MB`,
    `📸 Snapshots: ${snapshotHistory.length}`,
  ].join("\n");
}

function hideOverlay() {
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }
}

function printHistory() {
  console.table(snapshotHistory);
}

function detectLeak() {
  if (snapshotHistory.length < 2) return null;

  const baseline = snapshotHistory[0];
  const current = snapshotHistory[snapshotHistory.length - 1];

  if (baseline.jsHeapUsed === "N/A" || current.jsHeapUsed === "N/A") {
    return { leak: false, reason: "performance.memory not available" };
  }

  const heapGrowth =
    parseFloat(current.jsHeapUsed) - parseFloat(baseline.jsHeapUsed);
  const textureGrowth = current.pixiTextures - baseline.pixiTextures;

  const result = {
    heapGrowthMB: heapGrowth.toFixed(1),
    textureGrowth,
    suspectLeak: heapGrowth > 50 || textureGrowth > 100,
  };

  console.log(
    `%c[MemoryMonitor] Heap Δ: ${result.heapGrowthMB} MB | Textures Δ: ${result.textureGrowth}`,
    result.suspectLeak
      ? "color: red; font-weight: bold"
      : "color: green"
  );

  // ส่ง Sentry ทุกครั้งที่ detectLeak ถูกเรียก — ทั้งดีและไม่ดี
  if (result.suspectLeak && !leakReportCooldown) {
    leakReportCooldown = true;
    reportToSentry("leak-detected", {
      level: "error",
      suspectLeak: true,
      ...result,
      ...current,
      history: snapshotHistory.slice(-10),
    });
    setTimeout(() => { leakReportCooldown = false; }, 120000);
  } else if (!result.suspectLeak) {
    reportToSentry("health-check", {
      level: "info",
      suspectLeak: false,
      ...result,
      ...current,
    });
  }

  return result;
}

// ─── Start / Stop ────────────────────────────────────────

function startMonitoring() {
  if (isActive) return;
  isActive = true;

  snapshotHistory = [];
  leakReportCooldown = false;
  takeSnapshot("init");
  renderOverlay();

  intervalId = setInterval(() => {
    takeSnapshot("auto");
    renderOverlay();
  }, 5000);

  console.log(
    "%c[MemoryMonitor] ON — Ctrl+Shift+M to toggle",
    "color: cyan; font-weight: bold"
  );

  // Report monitor started
  reportToSentry("monitor-started", {
    message: "MemoryMonitor activated",
    jsHeapUsed: snapshotHistory[0]?.jsHeapUsed || "N/A",
    pixiTextures: snapshotHistory[0]?.pixiTextures || 0,
  });
}

function stopMonitoring() {
  if (!isActive) return;
  isActive = false;

  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  hideOverlay();
  const finalSnapshot = snapshotHistory[snapshotHistory.length - 1];

  // Report monitor stopped with final stats
  reportToSentry("monitor-stopped", {
    message: "MemoryMonitor deactivated",
    duration_snapshots: snapshotHistory.length,
    finalHeapUsed: finalSnapshot?.jsHeapUsed || "N/A",
    finalTextures: finalSnapshot?.pixiTextures || 0,
  });

  snapshotHistory = [];

  console.log("%c[MemoryMonitor] OFF", "color: orange");
}

function toggleMonitoring() {
  if (isActive) {
    stopMonitoring();
  } else {
    startMonitoring();
  }
}

// ─── Public API ──────────────────────────────────────────

/**
 * เรียกตอน PIXI app สร้างเสร็จ
 * - Dev:  เปิดทันที
 * - Prod: register keyboard shortcut + check URL param
 */
export function initMemoryMonitor(pixiApp) {
  trackedPixiApp = pixiApp;

  // Auto-detect pin + email แล้วตั้ง Sentry global tags ทันที
  const autoPin = extractPinFromURL();
  const autoEmail = extractEmailFromToken();
  if (autoPin || autoEmail) {
    setSentryTags({
      pin: autoPin,
      email: autoEmail,
    });
  }
  window.__memMonitor = {
    snapshot: (label) => {
      const s = takeSnapshot(label);
      renderOverlay();
      return s;
    },
    history: printHistory,
    detectLeak,
    toggle: toggleMonitoring,
    start: startMonitoring,
    stop: stopMonitoring,
    setSentryTags,
    get active() {
      return isActive;
    },
    getHistory: () => [...snapshotHistory],
  };

  const hasUrlParam = new URLSearchParams(window.location.search).has(
    "memmon"
  );

  if (hasUrlParam) {
    startMonitoring();
  }

  // Always register keyboard shortcut
  keyHandler = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "M") {
      e.preventDefault();
      toggleMonitoring();
    }
  };
  document.addEventListener("keydown", keyHandler);
  console.log(
    "%c[MemoryMonitor] Press Ctrl+Shift+M or use window.__memMonitor.toggle()",
    "color: gray"
  );
}

/**
 * เรียกทุกครั้งที่ destroy / เปลี่ยน scene
 */
export function logPixiStats(label = "") {
  if (!trackedPixiApp) return;

  const snap = takeSnapshot(label);
  renderOverlay();

  const renderer = trackedPixiApp.renderer;
  const gl = renderer?.gl;

  const stats = {
    ...snap,
    glTextures: gl ? gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) : "N/A",
    drawCalls: renderer?.plugins?.batch?._drawCalls ?? "N/A",
  };

  console.log(`%c[MemoryMonitor] ${label}`, "color: yellow", stats);

  // ส่ง Sentry ตอนเปลี่ยน scene / destroy
  if (isActive) {
    reportToSentry(label || "pixi-stats", {
      level: "info",
      suspectLeak: false,
      ...stats,
    });
  }

  return stats;
}

/**
 * Cleanup ตอน unmount
 */
export function destroyMemoryMonitor() {
  stopMonitoring();

  if (keyHandler) {
    document.removeEventListener("keydown", keyHandler);
    keyHandler = null;
  }

  delete window.__memMonitor;
  trackedPixiApp = null;
}
